import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Package, Plus } from "lucide-react";
import { useAccountant } from "@/contexts/AccountantContext";
import { getCurrencySymbol } from "@/lib/currencies";
import EmptyState from "@/components/EmptyState";
import KipaIcon from "@/components/kipa/KipaIcon";
import { ProductIcon } from "@/lib/productIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNIT_TYPES = [
  { value: "carton", label: "Carton" },
  { value: "box", label: "Box" },
  { value: "roll", label: "Roll" },
  { value: "pieces", label: "Pieces" },
  { value: "pack", label: "Pack" },
  { value: "bottle", label: "Bottle" },
];

function unitLabel(type) {
  return UNIT_TYPES.find((u) => u.value === type)?.label || type || "Pieces";
}

export default function InventoryScreen({ reloadKey }) {
  const { fmt, currency } = useAccountant();
  const sym = getCurrencySymbol(currency);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    axios.get(`${API}/inventory`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(load, [reloadKey]);

  const lowStock = items.filter(
    (i) => i.low_stock_threshold > 0 && i.stock_qty <= i.low_stock_threshold
  );

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="inventory-screen">
      <div className="flex-1 overflow-y-auto p-4">
        {lowStock.length > 0 && (
          <div
            className="bg-[var(--rl)] border border-[var(--rdim)] rounded-2xl p-3 mb-3"
            data-testid="low-stock-banner"
          >
            <div className="text-[var(--rd)] text-[12px] font-extrabold mb-1 flex items-center gap-1.5">
              <KipaIcon icon={AlertTriangle} size="sm" color="var(--color-error)" />
              {lowStock.length} item{lowStock.length > 1 ? "s" : ""} running low
            </div>
            <div className="text-[var(--tx2)] text-[12px] leading-snug">
              {lowStock.slice(0, 3).map((i) => i.name).join(" · ")}
              {lowStock.length > 3 ? ` +${lowStock.length - 3} more` : ""}
            </div>
          </div>
        )}

        <div className="bg-[var(--color-primary-light)] border border-[var(--color-primary-dim)] rounded-2xl p-3 mb-3">
          <div className="text-[var(--color-primary)] text-[12px] font-bold mb-1 flex items-center gap-1.5">
            <KipaIcon icon={Package} size="sm" color="var(--color-primary)" />
            Product inventory
          </div>
          <div className="text-[var(--tx2)] text-[12px] leading-snug">
            Set cost price and selling price for each product. Profit is calculated automatically when you record sales.
          </div>
        </div>

        <button
          onClick={() => setCreating(true)}
          data-testid="add-inventory"
          className="w-full py-3 rounded-2xl border-2 border-dashed border-[var(--br2)] text-[var(--tx2)] text-[13px] font-bold mb-3 inline-flex items-center justify-center gap-2"
        >
          <KipaIcon icon={Plus} size="sm" />
          Add product
        </button>

        {items.length === 0 && (
          <EmptyState
            icon={<KipaIcon icon={Package} size="xl" color="var(--color-primary)" />}
            headline="No products tracked yet"
            body="Add your products with cost and selling prices to track stock and profit."
            testid="inventory-empty"
          />
        )}

        {items.length > 0 && (
          <div className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-3 card-sh">
            <div className="text-[11px] font-bold uppercase text-[var(--tx3)]">Total stock value</div>
            <div className="text-[20px] font-black text-[var(--ac)]">
              {fmt(items.reduce((s, i) => s + (Number(i.stock_qty) || 0) * (Number(i.cost_price) || 0), 0))}
            </div>
          </div>
        )}

        {items.map((it) => {
          const isLow = it.low_stock_threshold > 0 && it.stock_qty <= it.low_stock_threshold;
          const sell = it.selling_price ?? it.unit_price ?? 0;
          const cost = it.cost_price ?? 0;
          const stockVal = (Number(it.stock_qty) || 0) * cost;
          return (
            <button
              key={it.id}
              onClick={() => setEditing(it)}
              data-testid={`inv-${it.id}`}
              className="w-full bg-white border border-[var(--br)] rounded-2xl p-3 mb-2 flex items-center gap-3 card-sh text-left"
              style={isLow ? { borderLeft: "4px solid var(--rd)" } : {}}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: isLow ? "var(--rl)" : "var(--color-primary-light)" }}
              >
                <ProductIcon name={it.name} size={22} lowStock={isLow} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-bold text-[var(--tx)] capitalize truncate">{it.name}</div>
                <div className="text-[11px] text-[var(--tx3)]">
                  Cost {fmt(cost)} · Sell {fmt(sell)} · Value {fmt(stockVal)}
                </div>
                <div className="text-[10px] text-[var(--tx3)]">
                  Sold: {it.total_sold}
                  {(it.total_profit ?? 0) > 0 ? ` · Profit ${fmt(it.total_profit)}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-black text-[14px]" style={{ color: isLow ? "var(--rd)" : "var(--gn)" }}>
                  {Number(it.stock_qty)} left
                </div>
                <div className="text-[10px] text-[var(--tx3)]">
                  per {unitLabel(it.unit_type).toLowerCase()}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {(editing || creating) && (
        <ItemEditor
          item={editing}
          isNew={creating}
          sym={sym}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { load(); setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
}

function ItemEditor({ item, isNew, sym, onClose, onSaved }) {
  const [name, setName] = useState(item?.name || "");
  const [stock, setStock] = useState(item?.stock_qty ?? 0);
  const [threshold, setThreshold] = useState(item?.low_stock_threshold ?? 5);
  const [costPrice, setCostPrice] = useState(item?.cost_price ?? 0);
  const [sellingPrice, setSellingPrice] = useState(item?.selling_price ?? item?.unit_price ?? 0);
  const [unitType, setUnitType] = useState(item?.unit_type || "pieces");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      alert("Product name required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        stock_qty: Number(stock),
        low_stock_threshold: Number(threshold),
        cost_price: Number(costPrice),
        selling_price: Number(sellingPrice),
        unit_price: Number(sellingPrice),
        unit_type: unitType,
      };
      if (isNew) {
        await axios.post(`${API}/inventory`, payload);
      } else {
        await axios.put(`${API}/inventory/${item.id}`, payload);
      }
      onSaved();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/inventory/${item.id}`);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const cost = Number(costPrice) || 0;
  const sell = Number(sellingPrice) || 0;
  const margin = sell - cost;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45" onClick={onClose}>
      <div
        className="w-full max-w-[430px] bg-white rounded-t-3xl p-5 pb-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.25s ease-out" }}
        data-testid="inv-editor"
      >
        <div className="w-12 h-1.5 bg-[var(--br2)] rounded-full mx-auto mb-4" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1">
          {isNew ? "Add product" : "Edit product"}
        </div>
        <div className="text-[18px] font-black text-[var(--tx)] mb-4 capitalize">
          {isNew ? "New product" : item.name}
        </div>

        <Field label="Product name">
          <input className="s-inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="inv-name" />
        </Field>
        <Field label="Unit type">
          <select className="s-inp" value={unitType} onChange={(e) => setUnitType(e.target.value)} data-testid="inv-unit-type">
            {UNIT_TYPES.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Cost price (${sym})`} hint="What you pay per unit">
            <input type="number" className="s-inp" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} data-testid="inv-cost" />
          </Field>
          <Field label={`Selling price (${sym})`} hint="Optional default when selling">
            <input type="number" className="s-inp" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} data-testid="inv-price" />
          </Field>
        </div>
        {sell > 0 && cost > 0 && (
          <div className="text-[11px] text-[var(--gn)] font-bold mb-3">
            Profit per unit: {sym}{margin.toLocaleString()}
          </div>
        )}
        <Field label="Stock quantity on hand">
          <input type="number" className="s-inp" value={stock} onChange={(e) => setStock(e.target.value)} data-testid="inv-stock" />
        </Field>
        <Field label="Low-stock alert" hint="Alert when stock ≤ this number. Set 0 to disable.">
          <input type="number" className="s-inp" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="inv-threshold" />
        </Field>

        <button
          onClick={save}
          disabled={saving}
          data-testid="inv-save"
          className="w-full bg-[var(--ac)] text-white rounded-2xl py-3 font-extrabold text-[14px] mb-2 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {!isNew && (
          <button
            onClick={remove}
            disabled={saving}
            className="w-full bg-[var(--rl)] text-[var(--rd)] rounded-2xl py-2.5 font-bold text-[13px] mb-2 disabled:opacity-60"
          >
            Delete Item
          </button>
        )}
        <button onClick={onClose} className="w-full text-[var(--tx3)] rounded-2xl py-2 text-[12px]">
          Cancel
        </button>

        <style>{`
          .s-inp {
            background: var(--sf2);
            border: 1.5px solid var(--br);
            border-radius: 12px;
            padding: 10px 14px;
            color: var(--tx);
            font-size: 15px;
            width: 100%;
            font-family: inherit;
            outline: none;
          }
          .s-inp:focus { border-color: var(--ac); }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[var(--tx3)] mt-1 leading-snug">{hint}</div>}
    </div>
  );
}
